import { useState } from "react";
import Seo from "../lib/Seo";
import { subscribe, SOCIALS } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { Arrow, Check, TYPE_ICONS } from "../components/Icons";

const TYPES = {
  overthinker: {
    name: "The Overthinker",
    tag: "DI_OVERTHINKER",
    challenge: "Your biggest challenge is noise.",
    body: "You don't struggle because you don't know enough. You might actually have too much information competing at once.",
    signs: ["Great range sessions that don't transfer", "Too many swing thoughts", "Constant fixing", "Struggling to commit"],
    focus: ["Simplify", "Commit", "Execute"],
  },
  reactor: {
    name: "The Reactor",
    tag: "DI_REACTOR",
    challenge: "Your biggest challenge is getting back to neutral.",
    body: "The shot ends. But your brain and body stay there.",
    signs: ["One bad hole becomes three", "Replaying mistakes", "Frustration changes decisions", "Emotion takes over execution"],
    focus: ["Recognise", "Reset", "Return"],
  },
  score: {
    name: "The Score Protector",
    tag: "DI_SCORE_PROTECTOR",
    challenge: "Your challenge appears when things are going well.",
    body: "You don't fear bad golf. You start protecting good golf.",
    signs: ["Thinking about personal bests", "Playing safe", "Changing approach late in the round", "Tight swings"],
    focus: ["Detach", "Process", "Finish"],
  },
  pressure: {
    name: "The Pressure Player",
    tag: "DI_PRESSURE_PLAYER",
    challenge: "Your biggest challenge appears when the moment gets bigger.",
    body: "Your swing hasn't disappeared. Your state has changed.",
    signs: ["First tee nerves", "Competition struggles", "Tightness under pressure", "A different swing when people watch"],
    focus: ["Regulate", "Trust", "Perform"],
  },
  drifter: {
    name: "The Drifter",
    tag: "DI_DRIFTER",
    challenge: "Your biggest challenge is maintaining state.",
    body: "You have the ability. You lose connection with it.",
    signs: ["Random mistakes", "Mental fatigue", "Loss of concentration", "Going through the motions"],
    focus: ["Energy", "Attention", "Presence"],
  },
};

// A = overthinker, B = reactor, C = score, D = pressure, E = drifter (fixed order)
const QUESTIONS = [
  { q: "When does your game usually disappear?", opts: [
    ["When I start thinking about too many swing thoughts", "overthinker"],
    ["Straight after a bad shot or mistake", "reactor"],
    ["When I realise I'm playing really well and there is a score to protect", "score"],
    ["When people are watching or the pressure increases", "pressure"],
    ["Random moments where I just lose focus", "drifter"],
  ]},
  { q: "You hit one terrible shot. What happens next?", opts: [
    ["I immediately try to work out what went wrong technically", "overthinker"],
    ["I get frustrated and struggle to move on", "reactor"],
    ["I start worrying that the round is slipping away", "score"],
    ["I become aware of what others think", "pressure"],
    ["I switch off mentally for a few holes", "drifter"],
  ]},
  { q: "Standing over an important shot, what normally happens?", opts: [
    ["My brain gets noisy with instructions", "overthinker"],
    ["I'm still thinking about previous mistakes", "reactor"],
    ["I start thinking about what happens if it goes wrong", "score"],
    ["My body feels different or tense", "pressure"],
    ["I struggle to fully commit", "drifter"],
  ]},
  { q: "Your best golf normally happens when:", opts: [
    ["I stop thinking and just play", "overthinker"],
    ["I stay calm regardless of the outcome", "reactor"],
    ["I forget about the score completely", "score"],
    ["I feel confident and comfortable", "pressure"],
    ["I stay present for the whole round", "drifter"],
  ]},
  { q: "Which sentence sounds most like you?", opts: [
    ['"I probably know too much about my swing."', "overthinker"],
    ['"One bad shot can ruin my mood."', "reactor"],
    ['"I hate knowing I\'m on for a good score."', "score"],
    ['"I play differently when it matters."', "pressure"],
    ['"Sometimes I just disappear mentally."', "drifter"],
  ]},
  { q: "What advice annoys you the most?", opts: [
    ['"Stop thinking."', "overthinker"],
    ['"Just forget about it."', "reactor"],
    ['"Don\'t think about your score."', "score"],
    ['"Just relax."', "pressure"],
    ['"Stay focused."', "drifter"],
  ]},
  { q: "What usually happens near the end of a good round?", opts: [
    ["I start analysing what I'm doing differently", "overthinker"],
    ["A mistake can trigger frustration", "reactor"],
    ["I become aware of the score I could shoot", "score"],
    ["I start feeling pressure build", "pressure"],
    ["My concentration fades", "drifter"],
  ]},
  { q: "If you could improve one thing mentally, it would be:", opts: [
    ["Quiet my mind", "overthinker"],
    ["Reset faster", "reactor"],
    ["Stay present when playing well", "score"],
    ["Perform when pressure arrives", "pressure"],
    ["Maintain focus for 18 holes", "drifter"],
  ]},
];

const KEYS = ["A", "B", "C", "D", "E"];
const FB = "https://www.facebook.com/groups/1602746194599646";

export default function Quiz() {
  const [step, setStep] = useState(-1); // -1 intro, 0..7 questions, 8 result
  // answers[step] = array of option indices, kept in click order (drives tie-break)
  const [answers, setAnswers] = useState(QUESTIONS.map(() => []));
  const [resultKey, setResultKey] = useState(null);
  // form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [emailBad, setEmailBad] = useState(false);

  const progress = step < 0 ? 0 : step >= QUESTIONS.length ? 100 : (step / QUESTIONS.length) * 100;

  const toggle = (idx) => {
    setAnswers((prev) => {
      const next = prev.map((sel) => [...sel]);
      const sel = next[step];
      const at = sel.indexOf(idx);
      if (at >= 0) sel.splice(at, 1);
      else sel.push(idx); // click order preserved for tie-break
      return next;
    });
  };

  const advance = () => {
    if (answers[step].length === 0) return;
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Flatten every selection (question order, click order within a question)
      // into type keys, count them, and break ties by the most recent selection.
      const flat = [];
      answers.forEach((sel, qi) => sel.forEach((oi) => flat.push(QUESTIONS[qi].opts[oi][1])));
      const counts = {};
      flat.forEach((k) => { counts[k] = (counts[k] || 0) + 1; });
      const best = Math.max(...Object.values(counts));
      let rk = null;
      for (let i = flat.length - 1; i >= 0; i--) {
        if (counts[flat[i]] === best) { rk = flat[i]; break; }
      }
      setResultKey(rk);
      setStep(QUESTIONS.length);
      trackEvent("quiz_completed", { result: rk });
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const submit = async () => {
    setErr("");
    if (!email.trim() || !email.includes("@")) {
      setEmailBad(true);
      setTimeout(() => setEmailBad(false), 2200);
      return;
    }
    setBusy(true);
    try {
      const t = TYPES[resultKey];
      await subscribe({ first_name: name.trim(), email: email.trim(), tags: [t.tag], quiz_result: t.name });
      trackEvent("quiz_profile_signup", { result: resultKey });
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const t = resultKey ? TYPES[resultKey] : null;

  return (
    <div data-testid="quiz-app">
      <Seo
        title="The Dialed In Golfer Profile | Where does your golf game really break down?"
        description="Take the 2-minute golfer profile and discover what type of golfer you are when pressure arrives."
      />
      <div className="qz-progress" data-testid="quiz-progress"><i style={{ width: `${progress}%` }} /></div>

      <div className="qz-stage">
        {step === -1 && (
          <div className="qz-screen" data-testid="quiz-intro">
            <div className="label">The Dialed In Golfer Profile</div>
            <h1 className="disp qz-intro-h">Where does your golf game <span className="g">really break down?</span></h1>
            <p className="qz-intro-sub">
              You know the swing. You know the shot. But sometimes the golfer holding the club changes. Take the 2-minute profile and discover what type of golfer you are when pressure arrives.
            </p>
            <button className="cta" onClick={() => setStep(0)} data-testid="quiz-start-btn">
              Find my golfer type <Arrow />
            </button>
          </div>
        )}

        {step >= 0 && step < QUESTIONS.length && (
          <div className="qz-screen" key={step} data-testid="quiz-question-screen">
            <div className="qz-count" data-testid="quiz-count">Question {step + 1} / {QUESTIONS.length}</div>
            <h2 className="qz-q" data-testid="quiz-question">{QUESTIONS[step].q}</h2>
            <p className="qz-hint" data-testid="quiz-hint">Select all that apply.</p>
            <div>
              {QUESTIONS[step].opts.map((opt, i) => {
                const sel = answers[step].includes(i);
                return (
                  <button
                    key={i}
                    className={`qz-opt${sel ? " picked" : ""}`}
                    onClick={() => toggle(i)}
                    aria-pressed={sel}
                    data-testid={`quiz-option-${i + 1}`}
                  >
                    <span className="qz-key">{sel ? <Check /> : KEYS[i]}</span>
                    <span>{opt[0]}</span>
                  </button>
                );
              })}
            </div>
            <div className="qz-nav">
              <button className="qz-back" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="quiz-back-btn">
                <span style={{ transform: "rotate(180deg)", display: "inline-flex" }}><Arrow size={16} /></span> Back
              </button>
              <button className="cta" onClick={advance} disabled={answers[step].length === 0} data-testid="quiz-next-btn">
                {step === QUESTIONS.length - 1 ? "See my golfer type" : "Next"} <Arrow />
              </button>
            </div>
          </div>
        )}

        {step === QUESTIONS.length && t && (
          <div className="qz-screen" data-testid="quiz-result">
            <div className="qz-result-label">Your golfer type</div>
            <div className="qz-type-head">
              <span className="qz-type-icon" aria-hidden="true">{TYPE_ICONS[resultKey]}</span>
              <h2 className="qz-result-type" data-testid="quiz-result-type">{t.name}.</h2>
            </div>
            <p className="qz-challenge" data-testid="quiz-result-challenge">{t.challenge}</p>
            <p className="qz-result-desc" data-testid="quiz-result-desc">{t.body}</p>

            <div className="qz-signs">
              <div className="qz-block-label">Common signs</div>
              <div data-testid="quiz-result-signs">
                {t.signs.map((s, i) => <div className="qz-sign" key={i}><span>{s}</span></div>)}
              </div>
            </div>

            <div className="qz-focus" data-testid="quiz-result-focus">
              <div className="qz-block-label">Your Dialed In focus</div>
              <div className="qz-focus-row">
                {t.focus.map((w, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 16 }}>
                    {i > 0 && <Arrow size={22} />}
                    <span>{w}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="qz-capture">
              {done ? (
                <div className="ty-block" data-testid="quiz-thankyou">
                  <h3 className="ty-head">You're in.</h3>
                  <p className="ty-body">Your personalised breakdown is on its way. Keep an eye on your inbox — and if anything rings true, hit reply. Jason reads every one.</p>
                </div>
              ) : (
                <div>
                  <h3 className="qz-capture-h">Your full profile <span className="g">is ready.</span></h3>
                  <p className="qz-capture-sub">
                    Enter your email and we'll send your personalised {t.name} breakdown — what's happening, why it happens, and what to do about it.
                  </p>
                  <input type="text" className="field" placeholder="First name" autoComplete="given-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="quiz-first-name" />
                  <input type="email" className={`field${emailBad ? " field-err" : ""}`} placeholder="Email address" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quiz-email" />
                  <div className="submit-row">
                    <button className="cta" onClick={submit} disabled={busy} data-testid="quiz-submit-btn">
                      {busy ? "Sending" : "Reveal my full profile"} <Arrow />
                    </button>
                  </div>
                  <p className={`form-fine${err ? " err" : ""}`} data-testid="quiz-fine">{err || "No spam. No selling. Unsubscribe any time."}</p>
                </div>
              )}

              <div className="qz-community" data-testid="quiz-community">
                <h3 className="qz-community-h">Join the <span className="g">journey.</span></h3>
                <p className="qz-community-sub">Dialed In is being built with golfers, for golfers. Follow the process as we explore brain, body, state and performance.</p>
                <a className="cta cta-ghost" href={FB} target="_blank" rel="noopener noreferrer" data-testid="quiz-community-cta">
                  Join the Dialed In community <Arrow />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
